import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, UtensilsCrossed, Building2,
  Send, Newspaper, Zap, Settings, LogOut, ChevronLeft,
  ChevronRight, Waves, Bell, Brain, Rss, CalendarDays,
  TrendingUp, Instagram, Search, MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { path: '/dashboard',   icon: LayoutDashboard, label: 'Dashboard',    testid: 'sidebar-nav-dashboard' },
      { path: '/map',         icon: MapPin,          label: 'Hamptons Map', testid: 'sidebar-nav-map'       },
    ]
  },
  {
    label: 'Contacts & People',
    items: [
      { path: '/contacts', icon: Users, label: 'Contacts', testid: 'sidebar-nav-contacts' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/restaurants', icon: UtensilsCrossed, label: 'Restaurants', testid: 'sidebar-nav-restaurants' },
      { path: '/spaces', icon: Building2, label: 'Spaces', testid: 'sidebar-nav-spaces' },
      { path: '/content', icon: CalendarDays, label: 'Content', testid: 'sidebar-nav-content' },
    ]
  },
  {
    label: 'Intelligence',
    items: [
      { path: '/outreach',        icon: Send,        label: 'Outreach',         testid: 'sidebar-nav-outreach'   },
      { path: '/intelligence',    icon: Newspaper,   label: 'Intel Hub',        testid: 'sidebar-nav-intel'      },
      { path: '/news',            icon: Rss,         label: 'Live News',        testid: 'sidebar-nav-news'       },
      { path: '/capital',         icon: TrendingUp,  label: 'Capital Hub',      testid: 'sidebar-nav-capital'    },
      { path: '/instagram-intel', icon: Instagram,   label: 'Instagram Intel',  testid: 'sidebar-nav-ig-intel'   },
    ]
  },
  {
    label: 'System',
    items: [
      { path: '/agent',       icon: Brain,       label: 'AI Agent',       testid: 'sidebar-nav-agent'      },
      { path: '/enrichment',  icon: Search,      label: 'Enrich Contacts',testid: 'sidebar-nav-enrichment' },
      { path: '/automation',  icon: Zap,         label: 'Automation',     testid: 'sidebar-nav-automation' },
      { path: '/settings',    icon: Settings,    label: 'Settings',       testid: 'sidebar-nav-settings'   },
    ]
  },
];

export default function AppShell({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const getPageTitle = () => {
    const pathMap = {
      '/dashboard': 'Dashboard',
      '/map': 'Hamptons Map',
      '/contacts': 'Contacts',
      '/restaurants': 'Restaurants',
      '/spaces': 'Commercial Spaces',
      '/content': 'Content Scheduler',
      '/outreach': 'Outreach Sequences',
      '/intelligence': 'Intelligence Hub',
      '/news': 'Live News Feed',
      '/capital': 'Capital Hub',
      '/instagram-intel': 'Instagram Intel',
      '/enrichment': 'Contact Enrichment',
      '/automation': 'Automation Hub',
      '/agent': 'AI Intelligence Agent',
      '/settings': 'Settings',
    };
    return pathMap[location.pathname] || 'Hamptons Hospitality CRM';
  };

  return (
    <>
      {/* Sidebar */}
      <div className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`} data-testid="sidebar">
        {/* Logo */}
        <div className="px-4 py-5 border-b border-border flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Waves size={16} className="text-primary-foreground" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <div className="font-semibold text-sm text-foreground leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Hamptons XP</div>
              <div className="text-xs text-muted-foreground leading-tight">CRM</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-3 px-2">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              {!collapsed && (
                <div className="nav-section-label">{section.label}</div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={item.testid}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <item.icon size={18} className="flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        {/* User profile */}
        <div className="border-t border-border p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors">
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                    {user?.name?.[0]?.toUpperCase() || 'S'}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 text-left overflow-hidden">
                    <div className="text-sm font-semibold text-foreground truncate">{user?.name || 'Sam'}</div>
                    <div className="text-xs text-muted-foreground truncate">{user?.email || ''}</div>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => window.location.href = '/settings'}>
                <Settings size={14} className="mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-muted transition-colors z-10"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </div>

      {/* Main area */}
      <div className="main-content">
        {/* Topbar */}
        <div className="topbar">
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {getPageTitle()}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="w-9 h-9" data-testid="topbar-notifications">
              <Bell size={16} />
            </Button>
          </div>
        </div>

        {/* Page content */}
        <div className="page-content">
          {children}
        </div>
      </div>
    </>
  );
}
